import React, { useCallback, useLayoutEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { makeStyles } from '@material-ui/core/styles'
import { Button, CircularProgress, TextField, Typography } from '@material-ui/core'

import { settings } from '../../store/constants'

// THE TWO ENQUIRY PAGES, /corporate and /operators.
//
// Each one is a heading, a short promise and a form, so the promise is the
// only copy on the page and it has to do the whole job. Both lines are built
// out of facts the rest of the site already states and evidences: insured and
// vetted washers (pages/home/sections/who-turns-up), our own water and power,
// every London borough (app/helpers/seo_helper.rb), washed by hand at the kerb.
//
// Nothing here claims an invoicing arrangement or a payout schedule, because
// neither exists in the codebase to be true about. The meta descriptions in
// MetaTags::Helper used to; see the 5 October copy report.
const COPY = {
  corporate: {
    title: 'Fleet and corporate car washing',
    blurb:
      'Company fleets, dealerships and workplaces across London, washed by hand where the cars are parked. Professional valeters, vetted and fully insured, bringing their own water and power. Tell us what you need and you will hear from us within one business day.',
    helmet: 'Fleet & corporate car washing | GoWashMyCar',
    success: 'Thanks. We have your details, and you will hear from us within one business day.',
    showCompany: true
  },
  operator: {
    title: 'Become an operator',
    blurb:
      'Washing for GoWashMyCar across London, in your own van, on the hours you choose. We have been washing cars here since 2016, and the work comes to you through the app. Tell us about your experience and the areas you cover.',
    helmet: 'Become a mobile car wash operator | GoWashMyCar',
    success: 'Thanks for applying. We have your details and will be in touch about the next steps.',
    showCompany: false
  }
}

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: '60vh',
    padding: theme.spacing(5, 2),
    maxWidth: 520,
    margin: '0 auto'
  },
  title: {
    fontWeight: 700,
    fontSize: 28,
    textAlign: 'center'
  },
  blurb: {
    color: '#4b5563',
    textAlign: 'center',
    margin: theme.spacing(1.5, 0, 3),
    lineHeight: 1.6
  },
  field: {
    width: '100%',
    marginTop: theme.spacing(2)
  },
  submitBtn: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(1.5, 5),
    fontWeight: 600,
    fontSize: 15,
    textTransform: 'none',
    width: '100%'
  },
  error: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 6,
    padding: theme.spacing(1.5, 2),
    color: '#991b1b',
    marginTop: theme.spacing(2),
    width: '100%',
    textAlign: 'center',
    fontSize: 14
  },
  successCard: {
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: 8,
    padding: theme.spacing(4),
    textAlign: 'center',
    marginTop: theme.spacing(2),
    width: '100%'
  },
  backLink: {
    marginTop: theme.spacing(3),
    color: '#6b7280',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontSize: 14
  }
}))

const ContactForm = ({ topic }) => {
  const classes = useStyles()
  const copy = COPY[topic]

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [company, setCompany] = useState('')
  const [message, setMessage] = useState('')
  const [website, setWebsite] = useState('') // honeypot: hidden, real users leave blank
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [submitted, setSubmitted] = useState(false)

  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const handleSubmit = useCallback(
    async e => {
      e.preventDefault()
      setSubmitting(true)
      setError(null)

      try {
        const res = await fetch(`${settings.host}/api/v2/contact`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ topic, name, email, phone, company, message, website })
        })

        if (!res.ok) {
          const data = await res.json().catch(() => ({}))
          setError(data.message || 'Something went wrong. Please try again.')
          setSubmitting(false)
          return
        }

        setSubmitted(true)
      } catch {
        setError('Network error. Please try again.')
        setSubmitting(false)
      }
    },
    [topic, name, email, phone, company, message, website]
  )

  if (submitted) {
    return (
      <div className={classes.root}>
        <Helmet title={copy.helmet} />
        <Typography className={classes.title}>Got it 🙌</Typography>
        <div className={classes.successCard}>
          <Typography variant="body1">{copy.success}</Typography>
        </div>
        <a className={classes.backLink} href="/">Back to GoWashMyCar</a>
      </div>
    )
  }

  return (
    <form className={classes.root} onSubmit={handleSubmit} noValidate>
      <Helmet title={copy.helmet} />
      <Typography className={classes.title}>{copy.title}</Typography>
      <Typography className={classes.blurb}>{copy.blurb}</Typography>

      {/* Honeypot: hidden from real users (off-screen, aria-hidden, not tabbable).
          Bots fill every field, so a non-blank value is silently dropped server-side. */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={website}
        onChange={e => setWebsite(e.target.value)}
        style={{ position: 'absolute', left: '-5000px', width: '1px', height: '1px', opacity: 0 }}
      />

      <TextField
        className={classes.field}
        label="Your name"
        variant="outlined"
        value={name}
        onChange={e => setName(e.target.value)}
        required
        inputProps={{ maxLength: 120 }}
      />
      <TextField
        className={classes.field}
        label="Email"
        type="email"
        variant="outlined"
        value={email}
        onChange={e => setEmail(e.target.value)}
        required
        inputProps={{ maxLength: 160 }}
      />
      <TextField
        className={classes.field}
        label="Phone (optional)"
        variant="outlined"
        value={phone}
        onChange={e => setPhone(e.target.value)}
        inputProps={{ maxLength: 40 }}
      />
      {copy.showCompany && (
        <TextField
          className={classes.field}
          label="Company"
          variant="outlined"
          value={company}
          onChange={e => setCompany(e.target.value)}
          inputProps={{ maxLength: 160 }}
        />
      )}
      <TextField
        className={classes.field}
        label={topic === 'operator' ? 'Tell us about your experience' : 'How can we help?'}
        variant="outlined"
        value={message}
        onChange={e => setMessage(e.target.value)}
        multiline
        minRows={4}
        maxRows={10}
        required
        inputProps={{ maxLength: 4000 }}
      />

      {error && <div className={classes.error}>{error}</div>}

      <Button
        className={classes.submitBtn}
        variant="contained"
        color="primary"
        type="submit"
        disabled={submitting || !name || !email || message.length < 10}
      >
        {submitting ? <CircularProgress size={22} color="inherit" /> : 'Send'}
      </Button>
    </form>
  )
}

export const CorporatePage = React.memo(() => <ContactForm topic="corporate" />)
export const OperatorsPage = React.memo(() => <ContactForm topic="operator" />)
